import React from 'react'
import { status as productStatus } from '@jarvis.oddle/core/tools/product'

import useAppSelector from 'src/hooks/useAppSelector'
import { selectOrder } from 'src/redux/order'
import { selectStore } from 'src/redux/stores'
import { selectDetailMenuSite, selectMenuMode } from '../redux/meta'
import { isObjectEmpty } from '../utils/object'

interface StoreUnavailability {
  unavailableStores?: {
    [storeId: string]: {
      endDateTime: number | null
    }
  }
  unavailableForAllStores?: boolean
}

interface StoreAvailabilityStatus {
  isAvailable: boolean
  isUnavailable: boolean
}

const StoreItemAvailabilityContext = React.createContext({
  unavailableResources: {},
  storeItemAvailability: {},
  selectedStore: {} as Oms.V4.Store,
  isPickup: false,
  pickupTime: null,
  initialized: false
})

// Helper function to get store availability status
const getStoreAvailabilityStatus = (
  store: Oms.V4.Store,
  categoryUnavailability: StoreUnavailability,
  productUnavailability: StoreUnavailability,
  pickupTime: Date | null,
  timezoneOffset: number
): StoreAvailabilityStatus => {
  const unavailableResource =
    categoryUnavailability.unavailableStores?.[store.id] ||
    productUnavailability.unavailableStores?.[store.id]

  if (!unavailableResource) return { isAvailable: true, isUnavailable: false }

  if (unavailableResource.endDateTime === null) {
    return { isAvailable: false, isUnavailable: true }
  }

  const isUnavailable =
    unavailableResource.endDateTime >=
    new Date(pickupTime).setHours(
      new Date(pickupTime).getHours() + timezoneOffset / 60
    )

  return { isAvailable: !isUnavailable, isUnavailable }
}

export const StoreItemAvailabilityProvider = ({
  children
}: {
  children: React.ReactNode
}): JSX.Element => {
  const storeOrder = useAppSelector(selectOrder).storeOrder
  const stores = useAppSelector(selectStore).data
  const storeItemAvailability = useAppSelector(
    (state) => state['@storeItemAvailability']
  ).data
  const menuMode = useAppSelector(selectMenuMode)
  const [isTimeout, setIsTimeout] = React.useState(false)

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setIsTimeout(true)
    }, 3000) // 3 seconds timeout

    return () => clearTimeout(timer)
  }, []) // Only run once when component mounts

  const unavailableResources = React.useMemo(() => {
    return storeItemAvailability
      .filter(
        ({ unavailableStores }) => unavailableStores[storeOrder.data.storeId]
      )
      .reduce((acc, cur) => {
        acc[cur.resourceId] = cur
        return acc
      }, {})
  }, [storeOrder.data.storeId, storeItemAvailability])

  const selectedStore = React.useMemo(() => {
    if (storeOrder.data.defaultStoreId !== null) return null
    return stores.find((store) => store.id === storeOrder.data.storeId)
  }, [stores, storeOrder.data.defaultStoreId, storeOrder.data.storeId])

  const isPickup = React.useMemo(
    () => storeOrder.data.isPickup,
    [storeOrder.data.isPickup]
  )

  const pickupTime = React.useMemo(() => {
    if (!storeOrder.data.collectionDate || !storeOrder.data.collectionTime)
      return null
    return new Date(
      `${storeOrder.data.collectionDate} ${
        storeOrder.data.collectionTime.split(' to ')[0]
      }`
    )
  }, [storeOrder.data.collectionDate, storeOrder.data.collectionTime])

  const context = React.useMemo(
    () => ({
      storeItemAvailability: storeItemAvailability.reduce((acc, cur) => {
        acc[cur.resourceId] = cur
        return acc
      }, {}),
      unavailableResources,
      selectedStore,
      isPickup,
      pickupTime,
      initialized:
        menuMode === 'preview'
          ? true
          : isTimeout || !isObjectEmpty(storeOrder.data)
    }),
    [
      storeItemAvailability,
      unavailableResources,
      selectedStore,
      isPickup,
      pickupTime,
      menuMode,
      storeOrder.data,
      isTimeout
    ]
  )

  return (
    <StoreItemAvailabilityContext.Provider value={context}>
      {children}
    </StoreItemAvailabilityContext.Provider>
  )
}

export const useStoreItemAvailability = ({
  product,
  category
}: {
  product: Menu.MenuItem
  category: Menu.MenuData
}): {
  selectedStore: Oms.V4.Store
  isUnavailableAtSelectedStore: boolean
  isUnavailableAtAllStores: boolean
  isUnavailableAtSomeStores: boolean
  availableStores: Oms.V4.Store[]
  isPickup: boolean
  unavailableStores: Oms.V4.Store[]
  initialized: boolean
  isPermanentlyUnavailable: boolean
  isTemporarilyUnavailable: boolean
} => {
  const metaState = useAppSelector(selectDetailMenuSite)
  const { enableTimeLimitUnavailability } = metaState?.features || {}

  const {
    selectedStore,
    unavailableResources,
    storeItemAvailability,
    isPickup,
    pickupTime,
    initialized
  } = React.useContext(StoreItemAvailabilityContext)

  const categoryStoreLevelUnavailability = React.useMemo(
    () => storeItemAvailability[category.id] || {},
    [storeItemAvailability, category.id]
  )

  const productStoreLevelUnavailability = React.useMemo(
    () => storeItemAvailability[product.id] || {},
    [storeItemAvailability, product.id]
  )

  const stores = useAppSelector(selectStore).data.filter((store) => store.open)
  const timezoneOffset = React.useMemo(() => new Date().getTimezoneOffset(), [])

  // Calculate store availability regardless of enableTimeLimitUnavailability
  const { availableStores, unavailableStores } = React.useMemo(() => {
    if (!enableTimeLimitUnavailability) {
      const available = stores.filter(
        (store) =>
          !categoryStoreLevelUnavailability.unavailableStores?.includes(
            store.id
          ) &&
          !productStoreLevelUnavailability.unavailableStores?.includes(store.id)
      )

      const unavailable = stores.filter(
        (store) =>
          categoryStoreLevelUnavailability.unavailableStores?.includes(
            store.id
          ) ||
          productStoreLevelUnavailability.unavailableStores?.includes(store.id)
      )

      return { availableStores: available, unavailableStores: unavailable }
    }

    const available: Oms.V4.Store[] = []
    const unavailable: Oms.V4.Store[] = []

    stores.forEach((store) => {
      const { isAvailable, isUnavailable } = getStoreAvailabilityStatus(
        store,
        categoryStoreLevelUnavailability,
        productStoreLevelUnavailability,
        pickupTime,
        timezoneOffset
      )

      if (isAvailable) available.push(store)
      if (isUnavailable) unavailable.push(store)
    })

    return { availableStores: available, unavailableStores: unavailable }
  }, [
    stores,
    categoryStoreLevelUnavailability,
    productStoreLevelUnavailability,
    pickupTime,
    timezoneOffset,
    enableTimeLimitUnavailability
  ])

  const isUnavailableAtSelectedStore = React.useMemo(() => {
    if (!enableTimeLimitUnavailability) {
      return (
        unavailableResources[category.id] ||
        unavailableResources[product.id] ||
        categoryStoreLevelUnavailability.unavailableForAllStores ||
        productStoreLevelUnavailability.unavailableForAllStores
      )
    }

    if (!selectedStore || product.status !== productStatus.UNAVAILABLE)
      return false

    const { isUnavailable } = getStoreAvailabilityStatus(
      selectedStore,
      categoryStoreLevelUnavailability,
      productStoreLevelUnavailability,
      pickupTime,
      timezoneOffset
    )

    return isUnavailable
  }, [
    selectedStore,
    product.status,
    categoryStoreLevelUnavailability,
    productStoreLevelUnavailability,
    pickupTime,
    timezoneOffset,
    unavailableResources,
    category.id,
    product.id,
    enableTimeLimitUnavailability
  ])

  const isUnavailableAtAllStores = React.useMemo(
    () => unavailableStores.length >= stores.length,
    [unavailableStores.length, stores.length]
  )

  const isUnavailableAtSomeStores = React.useMemo(
    () => unavailableStores.length > 0 && availableStores.length > 0,
    [unavailableStores.length, availableStores.length]
  )

  const isPermanentlyUnavailable = React.useMemo(() => {
    if (!enableTimeLimitUnavailability) {
      return (
        categoryStoreLevelUnavailability.unavailableForAllStores ||
        productStoreLevelUnavailability.unavailableForAllStores
      )
    }

    return stores.every((store) => {
      const categoryUnavailable =
        categoryStoreLevelUnavailability.unavailableStores?.[store.id]
      const productUnavailable =
        productStoreLevelUnavailability.unavailableStores?.[store.id]

      return (
        categoryUnavailable?.endDateTime === null ||
        productUnavailable?.endDateTime === null
      )
    })
  }, [
    stores,
    categoryStoreLevelUnavailability,
    productStoreLevelUnavailability,
    enableTimeLimitUnavailability
  ])

  const isTemporarilyUnavailable = React.useMemo(() => {
    if (!enableTimeLimitUnavailability) return false

    return stores.every((store) => {
      const categoryUnavailable =
        categoryStoreLevelUnavailability.unavailableStores?.[store.id]
      const productUnavailable =
        productStoreLevelUnavailability.unavailableStores?.[store.id]

      return (
        typeof categoryUnavailable?.endDateTime === 'number' ||
        typeof productUnavailable?.endDateTime === 'number'
      )
    })
  }, [
    stores,
    categoryStoreLevelUnavailability,
    productStoreLevelUnavailability,
    enableTimeLimitUnavailability
  ])

  return {
    selectedStore,
    isUnavailableAtSelectedStore,
    isUnavailableAtAllStores,
    isUnavailableAtSomeStores,
    availableStores,
    isPickup,
    unavailableStores,
    initialized,
    isPermanentlyUnavailable,
    isTemporarilyUnavailable
  }
}

export default StoreItemAvailabilityContext
